import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Camera, X, Loader2, Image, Trash2, Upload, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function ChantierPhotoUpload({ chantierId, step, photos = [], onPhotoAdded }) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [blobUrls, setBlobUrls] = useState({});
  const [loadingPhotos, setLoadingPhotos] = useState({});

  const activePhotos = photos.filter(p => !p.is_deleted && (!step || p.step === step || !p.step));

  const loadPhoto = useCallback(async (photo) => {
    if (blobUrls[photo.id]) return;
    setLoadingPhotos(prev => ({ ...prev, [photo.id]: true }));
    try {
      const res = await fetch(`${API}/api/chantiers/${chantierId}/photos/${photo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrls(prev => ({ ...prev, [photo.id]: url }));
      }
    } catch (e) {
      console.error('Error loading photo:', e);
    } finally {
      setLoadingPhotos(prev => ({ ...prev, [photo.id]: false }));
    }
  }, [chantierId, token, blobUrls]);

  useEffect(() => {
    activePhotos.forEach(p => {
      if (!blobUrls[p.id]) loadPhoto(p);
    });
  }, [activePhotos.length]);

  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !chantierId) return;

    setUploading(true);
    let uploaded = 0;

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const url = `${API}/api/chantiers/${chantierId}/photos?step=${step || ''}`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (res.ok) {
          uploaded++;
          const data = await res.json();
          // Load the new photo blob immediately
          const photoRes = await fetch(`${API}/api/chantiers/${chantierId}/photos/${data.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (photoRes.ok) {
            const blob = await photoRes.blob();
            setBlobUrls(prev => ({ ...prev, [data.id]: URL.createObjectURL(blob) }));
          }
        } else {
          const err = await res.json();
          toast.error(`${file.name}: ${err.detail || 'Erreur'}`);
        }
      } catch {
        toast.error(`${file.name}: Erreur réseau`);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} ajoutée${uploaded > 1 ? 's' : ''}`);
      if (onPhotoAdded) onPhotoAdded();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (photoId) => {
    try {
      const res = await fetch(`${API}/api/chantiers/${chantierId}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Photo supprimée');
        if (blobUrls[photoId]) {
          URL.revokeObjectURL(blobUrls[photoId]);
          setBlobUrls(prev => { const n = { ...prev }; delete n[photoId]; return n; });
        }
        if (onPhotoAdded) onPhotoAdded();
      }
    } catch { toast.error('Erreur réseau'); }
  };

  if (!chantierId) return (
    <div className="text-center py-6 text-sm text-slate-400">
      Sauvegardez le chantier pour pouvoir ajouter des photos.
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Upload area */}
      <div className="border-2 border-dashed border-orange-200 rounded-lg p-4 text-center hover:border-orange-400 transition-colors">
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleUpload}
          disabled={uploading}
          className="hidden"
          id={`photo-upload-${step || 'all'}`}
          data-testid={`photo-upload-input-${step || 'all'}`}
        />
        <label htmlFor={`photo-upload-${step || 'all'}`} className="cursor-pointer flex flex-col items-center gap-2">
          {uploading ? (
            <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
          ) : (
            <Camera className="w-8 h-8 text-orange-400" />
          )}
          <div>
            <p className="text-sm font-medium text-slate-700">
              {uploading ? 'Upload en cours...' : 'Cliquez pour ajouter des photos'}
            </p>
            <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP — Max 20 Mo</p>
          </div>
        </label>
      </div>

      {/* WhatsApp message */}
      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
        <MessageCircle className="w-5 h-5 text-green-600 shrink-0" />
        <p className="text-xs text-green-700">
          Vous pouvez aussi envoyer vos photos via le <strong>groupe WhatsApp dédié</strong> du chantier.
        </p>
      </div>

      {/* Photo Gallery */}
      {activePhotos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-gallery">
          {activePhotos.map(photo => (
            <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-square" data-testid={`photo-${photo.id}`}>
              {blobUrls[photo.id] ? (
                <img
                  src={blobUrls[photo.id]}
                  alt={photo.original_filename}
                  className="w-full h-full object-cover"
                />
              ) : loadingPhotos[photo.id] ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-8 h-8 text-slate-300" />
                </div>
              )}
              {/* Overlay with info + delete */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                <div className="w-full px-2 py-1.5 bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
                  <span className="text-xs truncate flex-1">{photo.original_filename}</span>
                  <button
                    onClick={() => handleDelete(photo.id)}
                    className="p-1 hover:bg-white/20 rounded"
                    title="Supprimer"
                    data-testid={`delete-photo-${photo.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {photo.step && (
                <span className="absolute top-1 left-1 bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  E{photo.step}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
